import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const LINEAS = (emp) => [
  "PREPARANDO TU ESPACIO DE TRABAJO…",
  `CARGANDO EL CONTEXTO DE ${(emp || "TU EMPRESA").toUpperCase()}…`,
  "LISTO",
];

export default function Boot() {
  const nav = useNavigate();
  const { user, empresa, refreshMe } = useAuth();
  const [fase, setFase] = useState("carga"); // carga | bienvenida
  const [progreso, setProgreso] = useState(0);
  const [linea, setLinea] = useState(0);
  const destino = useRef("/");

  useEffect(() => {
    let timers = [];
    (async () => {
      const meRes = await refreshMe();
      let destinoRuta = "/";
      try {
        const { data } = await api.get("/mesa");
        if (!data.onboarding_completo || !data.rol_definido) destinoRuta = "/conocerte";
        else if (!data.tiene_perfil) destinoRuta = "/inicio";
        else destinoRuta = "/";
      } catch {}
      destino.current = destinoRuta;
      const primeraVez = meRes?.usuario?.primera_vez;

      if (primeraVez) {
        timers.push(setTimeout(() => setProgreso(100), 200));
        timers.push(setTimeout(() => setLinea(1), 2000));
        timers.push(setTimeout(() => setLinea(2), 4000));
        timers.push(setTimeout(() => setFase("bienvenida"), 6400));
        timers.push(setTimeout(async () => { await api.post("/usuario/marcar-visto"); nav(destino.current); }, 8400));
      } else {
        timers.push(setTimeout(() => nav(destino.current), 2000));
      }
    })();
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-negro flex flex-col items-center justify-center px-6 overflow-hidden">
      <AnimatePresence mode="wait">
        {fase === "carga" ? (
          <motion.div key="carga" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} className="flex flex-col items-center">
            <div className="relative flex items-center justify-center mb-14" style={{ width: 260, height: 260 }}>
              {/* Glow que respira */}
              <motion.div
                className="absolute rounded-full"
                style={{ width: 260, height: 260, background: "radial-gradient(circle, rgba(201,168,76,0.30) 0%, rgba(201,168,76,0.07) 45%, rgba(201,168,76,0) 70%)" }}
                animate={{ scale: [1, 1.18, 1], opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
              />
              {/* Halo dorado girando */}
              <motion.div
                className="absolute rounded-full"
                style={{
                  width: 205, height: 205,
                  background: "conic-gradient(from 0deg, rgba(201,168,76,0) 0%, rgba(255,224,130,0.95) 18%, rgba(201,168,76,0) 46%, rgba(201,168,76,0) 100%)",
                  WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 2px))",
                  mask: "radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 2px))",
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 3.2, repeat: Infinity, ease: "linear" }}
              />
              {/* Halo interior contra-girando, más tenue */}
              <motion.div
                className="absolute rounded-full"
                style={{
                  width: 152, height: 152,
                  background: "conic-gradient(from 180deg, rgba(201,168,76,0) 0%, rgba(201,168,76,0.55) 30%, rgba(201,168,76,0) 62%)",
                  WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 1px), #000 calc(100% - 1px))",
                  mask: "radial-gradient(farthest-side, transparent calc(100% - 1px), #000 calc(100% - 1px))",
                }}
                animate={{ rotate: -360 }}
                transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
              />
              {/* Casco flotando */}
              <motion.img
                src="/olimpo-helmet.png"
                alt="Olimpo"
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: [1, 1.05, 1], y: [0, -6, 0] }}
                transition={{
                  opacity: { duration: 0.9, ease: "easeOut" },
                  scale: { duration: 3, repeat: Infinity, ease: "easeInOut" },
                  y: { duration: 3, repeat: Infinity, ease: "easeInOut" },
                }}
                style={{ height: 100, width: "auto", position: "relative", zIndex: 2, filter: "drop-shadow(0 0 18px rgba(201,168,76,0.55))" }}
              />
              {/* Destello que barre el casco */}
              <motion.div
                className="absolute"
                style={{
                  width: 100, height: 100, zIndex: 3, mixBlendMode: "screen", pointerEvents: "none",
                  background: "linear-gradient(115deg, transparent 38%, rgba(255,255,255,0.55) 50%, transparent 62%)",
                  backgroundSize: "250% 100%",
                  WebkitMaskImage: "url(/olimpo-helmet.png)", WebkitMaskSize: "contain", WebkitMaskRepeat: "no-repeat", WebkitMaskPosition: "center",
                  maskImage: "url(/olimpo-helmet.png)", maskSize: "contain", maskRepeat: "no-repeat", maskPosition: "center",
                }}
                initial={{ backgroundPosition: "200% 0%" }}
                animate={{ backgroundPosition: ["200% 0%", "-100% 0%"] }}
                transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 1.6, ease: "easeInOut" }}
              />
            </div>
            <div className="w-[180px] h-[2px] bg-borde rounded-full overflow-hidden mb-6">
              <div className="h-full grad-dorado" style={{ width: `${progreso}%`, transition: "width 6s linear" }} />
            </div>
            <div className="mono-label text-gris h-4" style={{ fontSize: "11px" }}>{LINEAS(empresa?.nombre)[linea]}</div>
          </motion.div>
        ) : (
          <motion.div key="bienvenida" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.2 }} className="text-center">
            <h1 className="titulo text-[44px] mb-4">Bienvenido, {user?.nombre}</h1>
            <div className="mono-label text-dorado" style={{ letterSpacing: "0.18em" }}>{empresa?.nombre?.toUpperCase()}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
